import type { App } from "vue"

import * as Sentry from "@sentry/vue"

export const useSentry = (app: App) => {
  // Staging diagnostics stay in the test session, not production alert channels.
  if (import.meta.env.MODE === "staging") return
  const h = window.location.host
  if (h.startsWith("localhost") || h.startsWith("127.0.0.1")) return

  Sentry.init({
    app,
    dsn: "https://8ca9fa8de3d24f869afbe40c05bdeb91@o147846.ingest.sentry.io/4504603679457280",
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,

    // reportSentry 를 거치지 않고 전역 핸들러(onerror/unhandledrejection)로 바로
    // 잡히는 것들이 있어, 전송 실패는 여기서도 막는다. isExpected 의 ERR_NETWORK
    // 항목과 같은 이유이며 근거도 같다 — 응답이 오지 않은 요청은 우리가 고칠 것이 없다.
    // (PARTNER-FRONTEND-XW 가 이 경로로 들어왔다: TypeError: Failed to fetch)
    beforeSend(event) {
      const msg = String(event?.exception?.values?.[0]?.value || event?.message || "")
      if (/^(TypeError: )?Failed to fetch$/i.test(msg.trim())) return null
      if (/^Network ?Error$/i.test(msg.trim())) return null
      if (/NetworkError when attempting to fetch resource/i.test(msg)) return null
      return event
    },
  })
}

const REPORTED = "__sentryReported"

function normalizeError(err: unknown): Error {
  if (err instanceof Error) return err
  if (typeof err === "string") return new Error(err)
  try {
    return new Error(JSON.stringify(err))
  } catch {
    return new Error(String(err))
  }
}

/**
 * 400 으로 내려오지만 사용자가 화면에서 사유를 안내받고 스스로 고칠 수 있는 오류.
 * 우리가 볼 장애가 아니라 정상적인 입력 검증 결과다.
 *
 * - 1000 InvalidToken   : 로그인 만료. 인터셉터가 로그인 화면으로 보낸다.
 * - 2005 InvalidId      : 아이디 오류. LogIn.vue 가 화면에 사유를 띄운다.
 * - 2006 InvalidPassword: 비밀번호 오류. 위와 같다.
 *
 * 코드를 하나씩 적어 두는 이유는, "400 은 전부 제외" 같은 넓은 규칙이 진짜 결함까지
 * 삼키기 때문이다. 안내되는 것이 확인된 코드만 넣는다.
 */
const USER_CORRECTABLE_CODES = new Set(["1000", "2005", "2006"])

/**
 * 사용자가 폼에 채워 보낸 요청. 이 메서드로 나간 400 은 "입력값이 거절됐다"는 뜻이라
 * 화면에서 사유를 보여주는 것이 정상 흐름이다.
 *
 * 🚨 GET 은 일부러 뺐다. GET 의 파라미터는 사용자가 타이핑한 값이 아니라 우리 코드가
 * 만든 값이라, 서버가 거절했다면 그건 사용자 입력 오류가 아니라 우리 결함이다.
 * 실제로 이 구분으로 결함 2건이 드러났다 — `GET /purchases?page=0`("page is invalid",
 * TablePaginator 가 결과 0건일 때 0 페이지를 요청) 와 `GET /qualities/NaN/products`
 * (이탈 중 라우트 파라미터가 사라져 Number(undefined)=NaN). 둘 다 수정됐지만,
 * 같은 모양의 결함이 또 생기면 이 필터를 통과해 Sentry 에 남아야 한다.
 */
const USER_SUBMITTED_METHODS = new Set(["post", "put", "patch"])

/**
 * 사용자가 손쓸 수 없는 오류만 올린다.
 * - 위 USER_CORRECTABLE_CODES: 화면에서 사유가 안내되는 정상 흐름
 * - 요청 취소: 사용자가 화면을 떠난 것
 * - 요청 중단: 브라우저가 진행 중인 XHR 을 끊은 것
 * - 전송 실패: 응답 자체가 오지 않은 것 (아래 설명)
 * - 사용자가 제출한 폼의 검증 실패
 */
function isExpected(err: any): boolean {
  if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return true

  // 페이지 이탈·인앱브라우저(카카오톡 등) 종료로 브라우저가 XHR 을 끊으면
  // axios 가 xhr.js 의 onabort 에서 ECONNABORTED "Request aborted" 를 낸다.
  // 서버도 우리 코드도 실패하지 않았다.
  // 🚨 타임아웃도 같은 ECONNABORTED 를 쓰므로(clarifyTimeoutError 기본 false)
  //    메시지로 구분한다 — 타임아웃은 실제 신호라 계속 보고한다.
  if (err?.code === "ECONNABORTED" && /^Request aborted$/i.test(String(err?.message ?? ""))) {
    return true
  }

  // 🚨 ERR_NETWORK = 요청이 서버에 닿지도 못했다(xhr 의 onerror, status 0).
  // 우리가 고칠 수 있는 것이 없는 클라이언트 회선 문제다. 실측 근거:
  // 2026-08-06~10 의 6건이 전부 서로 다른 파트너 × 다른 페이지 × 다른 브라우저였고
  // (카카오톡·삼성인터넷 등 인앱브라우저 다수), 같은 시각 API 파드는 재시작 0 이었다.
  // 진짜 API 장애라면 동시에 여러 사용자가 한꺼번에 실패해 시간에 뭉쳐서 나타난다.
  //
  // ⚠️ 이걸 끄면 클라이언트에서 오는 장애 신호 하나가 사라진다. 다만 개별 이벤트는
  // 애초에 장애 경보로 못 쓴다 — 회선 끊김과 구분이 안 되기 때문이다. API 가용성은
  // 서버측에서 감시해야 한다(현재 ALB 5xx·타깃헬스 알람은 없다 — 별건으로 보고).
  if (err?.code === "ERR_NETWORK") return true

  const status = err?.response?.status
  const data = err?.response?.data
  const code = data?.code
  const method = String(err?.config?.method ?? "").toLowerCase()

  if (status === 400) {
    if (USER_CORRECTABLE_CODES.has(String(code))) return true

    // 사용자가 제출한 폼이 서버 검증에 걸린 것. 화면이 사유를 보여준다.
    if (USER_SUBMITTED_METHODS.has(method) && code != null) return true
  }

  // 406/0006 NotAcceptable = 서버가 **사유를 문장으로 명시해** 거절한 것.
  // 파트너 API 에서 이 코드가 나오는 경로는 로그인 하나뿐이고(판매중지 계정),
  // 그 사유는 이제 로그인·백도어 화면에 그대로 표시된다. 우리가 볼 장애가 아니다.
  // GET 을 제외하는 이유는 400 과 같다 — GET 파라미터는 우리 코드가 만든 값이다.
  if (status === 406 && String(code) === "0006" && USER_SUBMITTED_METHODS.has(method)) return true

  return false
}

/**
 * 에러를 Sentry 로 보낸다.
 *
 * 이 앱은 catch 에서 console.log 만 하고 아무것도 보고하지 않아 Sentry 프로젝트에
 * 90일간 이벤트가 0건이었다. 침묵이 무사고를 뜻하지 않았다.
 *
 * 인터셉터와 호출부가 같은 오류를 이중으로 올리지 않도록, 한 번 보고한 에러에는
 * 표시를 남긴다.
 */
export function reportSentry(err: unknown, ctx?: Record<string, any>) {
  if (err && typeof err === "object") {
    if ((err as any)[REPORTED]) return
    ;(err as any)[REPORTED] = true
  }

  if (isExpected(err)) return

  const e = normalizeError(err)

  Sentry.withScope((scope) => {
    if (ctx) scope.setExtras(ctx)

    const res = (err as any)?.response
    if (res) {
      scope.setExtras({
        status: res.status,
        url: (err as any)?.config?.url,
        method: (err as any)?.config?.method,
        body: typeof res.data === "string" ? res.data.slice(0, 500) : res.data,
      })

      // 🚨 axios 오류는 스택이 전부 xhr.js -> settle.js 라, Sentry 가 서로 다른
      // 결함을 "AxiosError: Request failed with status code N" 한 통에 묶는다.
      // 실제로 PARTNER-FRONTEND-XT 한 이슈에 400(폼 검증)·406(판매중지 로그인)·
      // 403(남의 아이템 접근)이 섞여 있었고, 406 을 고쳐 종결했더니 무관한 403 이
      // 도착해 같은 이슈가 되살아났다 — 무엇이 남았는지 판정할 수 없는 상태다.
      //
      // 상태코드와 서버 코드로 지문을 갈라 각 결함이 자기 이슈를 갖게 한다.
      // 하나를 고치면 그것만 닫히고, 새 결함은 새 이슈로 올라온다.
      const apiCode = res.data && typeof res.data === "object" ? (res.data as any).code : null
      scope.setFingerprint(["{{ default }}", String(res.status ?? "no-status"), String(apiCode ?? "no-code")])
    }

    scope.setLevel("error")
    Sentry.captureException(e)
  })
}
